import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  Grid,
  Divider,
  Alert,
  Chip,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  Fade,
  Slide,
  Tooltip,
  Skeleton,
  Stack,
  alpha,
  InputAdornment,
} from '@mui/material';
import {
  Person,
  Email,
  Business,
  CalendarToday,
  Save,
  Edit,
  CameraAlt,
  CheckCircle,
  Cancel,
  CloudUpload,
  Image,
  Delete,
  Edit as EditIcon,
  Lock,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { apiService, getApiBaseUrl } from '../services/apiService';
import { formatPlanDisplayLabel, getPlanChipSx, resolveUserPlanType } from '../utils/planDisplay';
import { getHostTenantSlug } from '../utils/tenantResolver';
import { ONBOARDING_SESSION_KEY } from '../utils/onboardingStorage';

interface ProfileFormData {
  firstName: string;
  lastName: string;
  email: string;
}

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const PROFILE_BRAND_GRADIENT = 'linear-gradient(135deg, #1d4ed8 0%, #0d9488 100%)';
const PROFILE_BRAND_GRADIENT_HOVER = 'linear-gradient(135deg, #1e40af 0%, #0f766e 100%)';
const PROFILE_SURFACE_SHADOW = '0 8px 24px rgba(15, 23, 42, 0.08)';

const getProfileApiErrorMessage = (error: any, fallback: string): string => {
  if (error?.response?.status === 413) {
    return 'The image is too large for the server. Use a profile photo under 5MB or a background image under 10MB.';
  }
  return error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;
};

const lastPasswordChangeLabel = (u: { passwordChangedAt?: string; createdAt?: string }) =>
  u.passwordChangedAt || u.createdAt;

type AuthUser = NonNullable<ReturnType<typeof useAuth>['user']>;

const parseOrganizationId = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }
    const parsed = parseInt(trimmed, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};

const PAID_PLAN_TYPES = new Set(['STANDARD', 'PROFESSIONAL', 'ENTERPRISE', 'PRO']);

const resolvePlanTypeAfterProfileUpdate = (
  currentPlan?: string,
  apiPlan?: string
): string | undefined => {
  const current = currentPlan?.trim();
  const fromApi = apiPlan?.trim();
  if (!fromApi) {
    return current;
  }
  if (
    current &&
    PAID_PLAN_TYPES.has(current.toUpperCase()) &&
    fromApi.toUpperCase() === 'FREE'
  ) {
    // Profile APIs can briefly resolve FREE when tenant context is missing; keep paid plan (e.g. PROFESSIONAL).
    return current;
  }
  return fromApi;
};

/** Preserve role/plan/images when profile APIs return partial or empty-string fields. */
const mergeProfileApiUser = (current: AuthUser, updated: Partial<AuthUser>): AuthUser => {
  const organizationId = parseOrganizationId(updated.organizationId) ?? current.organizationId;
  return {
    ...current,
    ...updated,
    organizationId,
    role: updated.role ?? current.role,
    organizationSlug: updated.organizationSlug?.trim()
      ? updated.organizationSlug
      : current.organizationSlug,
    planType: resolvePlanTypeAfterProfileUpdate(current.planType, updated.planType),
    profilePictureUrl:
      updated.profilePictureUrl !== undefined
        ? updated.profilePictureUrl?.trim() || undefined
        : current.profilePictureUrl,
    backgroundImageUrl:
      updated.backgroundImageUrl !== undefined
        ? updated.backgroundImageUrl?.trim() || undefined
        : current.backgroundImageUrl,
  };
};

const Profile: React.FC = () => {
  const { user, patchUser } = useAuth();
  const { organization, setOrganization } = useTenant();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [formData, setFormData] = useState<ProfileFormData>({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingBackground, setIsDraggingBackground] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const backgroundInputRef = React.useRef<HTMLInputElement>(null);
  const [organizationName, setOrganizationName] = useState<string>('');
  
  // Password change state
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState<PasswordFormData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const currentPasswordInputRef = useRef<HTMLInputElement>(null);
  const newPasswordInputRef = useRef<HTMLInputElement>(null);
  const confirmPasswordInputRef = useRef<HTMLInputElement>(null);
  const isEditingRef = useRef(false);

  const togglePasswordFieldVisibility = (
    inputRef: React.RefObject<HTMLInputElement | null>,
    setVisible: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    const input = inputRef.current;
    const selectionStart = input?.selectionStart ?? null;
    const selectionEnd = input?.selectionEnd ?? null;
    setVisible((prev) => !prev);
    window.setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      if (selectionStart !== null && selectionEnd !== null) {
        el.setSelectionRange(selectionStart, selectionEnd);
      }
    }, 0);
  };

  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  useEffect(() => {
    if (searchParams.get('changePassword') === 'required') {
      setIsChangingPassword(true);
      setMessage({
        text: 'Please set a new password to complete your account setup.',
        type: 'success',
      });
    }
  }, [searchParams]);

  useEffect(() => {
    if (!user || isEditingRef.current) {
      return;
    }
    setFormData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
    });
  }, [user?.id, user?.firstName, user?.lastName, user?.email]);

  // Load organization name
  useEffect(() => {
    let cancelled = false;

    const loadOrganization = async () => {
      if (!user) {
        return;
      }

      if (organization?.name || organization?.slug) {
        if (!cancelled) {
          setOrganizationName(organization.name || organization.slug || user.organizationSlug || 'N/A');
        }
        return;
      }

      try {
        const hostSlug = getHostTenantSlug();
        const orgs = await apiService.getOrganizations();
        if (cancelled) {
          return;
        }

        const tenantOrg =
          hostSlug && Array.isArray(orgs)
            ? orgs.find((org) => String(org?.slug || '').toLowerCase() === hostSlug)
            : undefined;

        if (tenantOrg?.name || tenantOrg?.slug) {
          setOrganizationName(tenantOrg.name || tenantOrg.slug);
          setOrganization(tenantOrg);
          return;
        }

        const primaryOrg = await apiService.getPrimaryOrganization();
        if (cancelled) {
          return;
        }
        if (primaryOrg?.name || primaryOrg?.slug) {
          setOrganizationName(primaryOrg.name || primaryOrg.slug);
          setOrganization(primaryOrg);
          return;
        }

        if (orgs && orgs.length > 0) {
          const org = orgs[0];
          setOrganizationName(org.name || org.slug);
          setOrganization(org);
        } else {
          setOrganizationName(user.organizationSlug || 'N/A');
        }
      } catch (error) {
        console.error('Failed to load organization:', error);
        if (!cancelled) {
          setOrganizationName(user.organizationSlug || 'N/A');
        }
      }
    };

    void loadOrganization();
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.organizationSlug, organization?.id, organization?.name, organization?.slug, setOrganization]);

  // Auto-hide success messages after 5 seconds
  useEffect(() => {
    if (message?.type === 'success') {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear message when user starts typing
    if (message) {
      setMessage(null);
    }
  };

  const validateForm = (): boolean => {
    if (!formData.firstName.trim()) {
      setMessage({ text: 'First name is required', type: 'error' });
      return false;
    }
    if (!formData.lastName.trim()) {
      setMessage({ text: 'Last name is required', type: 'error' });
      return false;
    }
    if (!formData.email.trim()) {
      setMessage({ text: 'Email is required', type: 'error' });
      return false;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setMessage({ text: 'Please enter a valid email address', type: 'error' });
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const payload = {
        ...formData,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
      };
      const updatedUser = await apiService.updateProfile(payload);

      if (user && updatedUser) {
        const userData = mergeProfileApiUser(user, updatedUser);
        patchUser(userData);
        if (
          organization &&
          userData.organizationId &&
          organization.id === userData.organizationId &&
          userData.planType
        ) {
          setOrganization({ ...organization, planType: userData.planType });
        }
        setFormData({
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: userData.email || '',
        });
      }

      setMessage({ text: 'Profile updated successfully!', type: 'success' });
      setIsEditing(false);
    } catch (error: any) {
      const errorMessage = getProfileApiErrorMessage(error, 'Failed to update profile');
      setMessage({
        text: errorMessage,
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
      });
    }
    setIsEditing(false);
    setMessage(null);
  };

  const getInitials = () => {
    if (!user) return 'U';
    const first = user.firstName?.charAt(0).toUpperCase() || '';
    const last = user.lastName?.charAt(0).toUpperCase() || '';
    return `${first}${last}` || 'U';
  };

  const getFullName = () => {
    if (!user) return 'User';
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User';
  };

  const resolvedPlanType = resolveUserPlanType(user?.planType, organization?.planType);
  const planLabel = formatPlanDisplayLabel(resolvedPlanType);
  const hasBackgroundImage = !!(user?.backgroundImageUrl);
  const planChipSx = getPlanChipSx(resolvedPlanType, { onDarkBackground: hasBackgroundImage });

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  const handlePictureUpload = async (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage({ text: 'Please select an image file', type: 'error' });
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ text: 'File size must be less than 5MB', type: 'error' });
      return;
    }

    setUploadingPicture(true);
    setMessage(null);

    try {
      const response = await apiService.uploadProfilePicture(file);

      if (user && response) {
        patchUser(mergeProfileApiUser(user, response));
      }

      setMessage({ text: 'Profile picture uploaded successfully!', type: 'success' });
    } catch (error: any) {
      const errorMessage = getProfileApiErrorMessage(error, 'Failed to upload profile picture');
      setMessage({
        text: errorMessage,
        type: 'error',
      });
    } finally {
      setUploadingPicture(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handlePictureUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handlePictureUpload(file);
    }
  };

  const getProfilePictureUrl = () => {
    if (user?.profilePictureUrl) {
      if (
        user.profilePictureUrl.startsWith('uploads/') ||
        user.profilePictureUrl.startsWith('/uploads/')
      ) {
        // Backend serves uploads under the server context-path `/api`.
        const relativePath = user.profilePictureUrl.replace(/^\/+/, '');
        // Cache-bust on image path only — profile name saves also bump updatedAt and must not reload the avatar.
        return `${getApiBaseUrl()}/${relativePath}?v=${encodeURIComponent(relativePath)}`;
      }
      return user.profilePictureUrl;
    }
    return null;
  };

  const getBackgroundImageUrl = () => {
    if (user?.backgroundImageUrl) {
      if (
        user.backgroundImageUrl.startsWith('uploads/') ||
        user.backgroundImageUrl.startsWith('/uploads/')
      ) {
        // Backend serves uploads under the server context-path `/api`.
        const relativePath = user.backgroundImageUrl.replace(/^\/+/, '');
        return `${getApiBaseUrl()}/${relativePath}`;
      }
      return user.backgroundImageUrl;
    }
    return null;
  };

  const handleBackgroundUpload = async (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage({ text: 'Please select an image file', type: 'error' });
      return;
    }

    // Validate file size (10MB for background images)
    if (file.size > 10 * 1024 * 1024) {
      setMessage({ text: 'File size must be less than 10MB', type: 'error' });
      return;
    }

    setUploadingBackground(true);
    setMessage(null);

    try {
      const response = await apiService.uploadBackgroundImage(file);

      if (user && response) {
        patchUser(mergeProfileApiUser(user, response));
      }

      setMessage({ text: 'Background image uploaded successfully!', type: 'success' });
    } catch (error: any) {
      const errorMessage = getProfileApiErrorMessage(error, 'Failed to upload background image');
      setMessage({
        text: errorMessage,
        type: 'error',
      });
    } finally {
      setUploadingBackground(false);
      // Reset file input
      if (backgroundInputRef.current) {
        backgroundInputRef.current.value = '';
      }
    }
  };

  const handleBackgroundFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleBackgroundUpload(file);
    }
  };

  const handleBackgroundDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingBackground(true);
  };

  const handleBackgroundDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingBackground(false);
  };

  const handleBackgroundDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingBackground(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleBackgroundUpload(file);
    }
  };

  const handleDeleteBackground = async () => {
    if (!window.confirm('Are you sure you want to remove the background image?')) {
      return;
    }

    setUploadingBackground(true);
    setMessage(null);

    try {
      const updatedUser = await apiService.deleteBackgroundImage();

      if (user && updatedUser) {
        patchUser(mergeProfileApiUser(user, updatedUser));
      }

      setMessage({ text: 'Background image removed successfully!', type: 'success' });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to remove background image';
      setMessage({
        text: errorMessage,
        type: 'error',
      });
    } finally {
      setUploadingBackground(false);
    }
  };

  const handlePasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear field error when user types
    setPasswordErrors((prev) => ({ ...prev, [name]: undefined }));
    if (message) {
      setMessage(null);
    }
  };

  const validateNewPassword = (pwd: string): string | undefined => {
    if (!pwd.trim()) return 'New password is required';
    if (pwd.length < 8) return 'Must be at least 8 characters';
    if (pwd.length > 16) return 'Must be at most 16 characters';
    if (!/[A-Z]/.test(pwd)) return 'Must contain at least one uppercase letter';
    if (!/[a-z]/.test(pwd)) return 'Must contain at least one lowercase letter';
    if (!/[0-9]/.test(pwd)) return 'Must contain at least one number';
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd)) return 'Must contain at least one special character';
    return undefined;
  };

  const validatePasswordForm = (): boolean => {
    const errors: typeof passwordErrors = {};
    let isValid = true;

    if (!passwordData.currentPassword.trim()) {
      errors.currentPassword = 'Current password is required';
      isValid = false;
    }

    const newPwdError = validateNewPassword(passwordData.newPassword);
    if (newPwdError) {
      errors.newPassword = newPwdError;
      isValid = false;
    }

    if (passwordData.currentPassword && passwordData.newPassword &&
        passwordData.currentPassword === passwordData.newPassword) {
      errors.newPassword = 'New password must be different from current password';
      isValid = false;
    }

    if (!passwordData.confirmPassword.trim()) {
      errors.confirmPassword = 'Please confirm your new password';
      isValid = false;
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    setPasswordErrors(errors);
    if (!isValid) {
      setMessage({ text: 'Please fix the validation errors below', type: 'error' });
    }
    return isValid;
  };

  const handleChangePassword = async () => {
    if (!validatePasswordForm()) {
      return;
    }

    setChangingPassword(true);
    setMessage(null);

    try {
      const response = await apiService.changePassword(passwordData);
      if (response.success) {
        const requiredPasswordSetup = searchParams.get('changePassword') === 'required';
        setMessage({ text: response.message || 'Password changed successfully!', type: 'success' });
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setPasswordErrors({});
        setIsChangingPassword(false);
        if (user) {
          patchUser({
            ...(response.updatedAt ? { updatedAt: response.updatedAt } : {}),
            ...(response.passwordChangedAt ? { passwordChangedAt: response.passwordChangedAt } : {}),
          });
        }
        if (requiredPasswordSetup) {
          navigate('/proxy-settings?onboarding=1', { replace: true });
          window.setTimeout(() => {
            try {
              sessionStorage.setItem(ONBOARDING_SESSION_KEY, '1');
            } catch {
              /* ignore */
            }
            window.dispatchEvent(new Event('crud:show-proxy-walkthrough'));
          }, 0);
        }
      }
    } catch (error: any) {
      setMessage({
        text: error.response?.data?.error || error.message || 'Failed to change password',
        type: 'error',
      });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleCancelPasswordChange = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setPasswordErrors({});
    setIsChangingPassword(false);
    setMessage(null);
  };

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">Please log in to view your profile.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, sm: 3 } }}>
      {/* Header Section */}
      <Fade in timeout={600}>
        <Box sx={{ mb: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 700, 
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
              background: PROFILE_BRAND_GRADIENT,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Profile Settings
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.95rem', maxWidth: 480 }}>
            Manage your personal information and account details
          </Typography>
        </Box>
      </Fade>

      {/* Success/Error Message */}
      <Slide direction="down" in={!!message} mountOnEnter unmountOnExit>
        <Box sx={{ mb: 3 }}>
          <Alert 
            severity={message?.type} 
            onClose={() => setMessage(null)}
            icon={message?.type === 'success' ? <CheckCircle /> : undefined}
            sx={{
              borderRadius: 2,
              boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
              '& .MuiAlert-icon': {
                fontSize: '1.5rem',
              },
            }}
          >
            {message?.text}
          </Alert>
        </Box>
      </Slide>

      <Grid container spacing={3} alignItems="stretch">
        {/* Profile Card */}
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <Fade in timeout={800} style={{ width: '100%' }}>
            <Card 
              elevation={0}
              sx={{
                width: '100%',
                borderRadius: 3,
                overflow: 'hidden',
                position: 'relative',
                border: '1px solid',
                borderColor: alpha('#0d9488', 0.24),
                boxShadow: PROFILE_SURFACE_SHADOW,
                background: getBackgroundImageUrl() 
                  ? `url(${getBackgroundImageUrl()}) center/cover no-repeat`
                  : '#ffffff',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: getBackgroundImageUrl()
                    ? 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%)'
                    : 'linear-gradient(135deg, rgba(29, 78, 216, 0.05) 0%, rgba(13, 148, 136, 0.05) 100%)',
                  zIndex: 0,
                },
              }}
            >
              {/* Background Image Edit Controls */}
              {false && <Box
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  zIndex: 10,
                  display: 'flex',
                  gap: 1,
                }}
              >
                <input
                  ref={backgroundInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={handleBackgroundFileInputChange}
                  style={{ display: 'none' }}
                />
                <Tooltip title={getBackgroundImageUrl() ? "Change background" : "Upload background"} arrow>
                  <IconButton
                    onClick={() => backgroundInputRef.current?.click()}
                    disabled={uploadingBackground}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'white',
                        transform: 'scale(1.1)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {uploadingBackground ? (
                      <CircularProgress size={20} />
                    ) : getBackgroundImageUrl() ? (
                      <EditIcon fontSize="small" />
                    ) : (
                      <Image fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                {getBackgroundImageUrl() && (
                  <Tooltip title="Remove background" arrow>
                    <IconButton
                      onClick={handleDeleteBackground}
                      disabled={uploadingBackground}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        color: 'error.main',
                        '&:hover': {
                          bgcolor: 'white',
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>}

              {/* Drag overlay */}
              {false && isDraggingBackground && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(29, 78, 216, 0.8)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 5,
                    border: '3px dashed white',
                  }}
                >
                  <CloudUpload sx={{ fontSize: 64, color: 'white', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                    Drop image here
                  </Typography>
                </Box>
              )}

              <CardContent sx={{ p: { xs: 3, sm: 4 }, position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Avatar Section */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3, flex: 1 }}>
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => !uploadingPicture && fileInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if (!uploadingPicture && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        fileInputRef.current?.click();
                      }
                    }}
                    sx={{
                      position: 'relative',
                      mb: 2,
                      cursor: uploadingPicture ? 'wait' : 'pointer',
                      transition: 'transform 0.3s ease',
                      outline: 'none',
                      '&:hover': {
                        transform: 'scale(1.05)',
                      },
                    }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    aria-label="Upload profile picture"
                  >
                    <Avatar
                      src={getProfilePictureUrl() || undefined}
                      sx={{
                        width: { xs: 120, sm: 140 },
                        height: { xs: 120, sm: 140 },
                        bgcolor: getBackgroundImageUrl() ? 'rgba(255, 255, 255, 0.2)' : alpha('#0d9488', 0.14),
                        color: getBackgroundImageUrl() ? 'white' : '#0d9488',
                        fontSize: { xs: '3rem', sm: '3.5rem' },
                        fontWeight: 600,
                        border: `4px solid ${
                          getBackgroundImageUrl()
                            ? (isDragging ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.3)')
                            : alpha('#0d9488', 0.3)
                        }`,
                        transition: 'all 0.3s ease',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                      }}
                    >
                      {uploadingPicture ? (
                        <CircularProgress sx={{ color: 'white' }} />
                      ) : (
                        getInitials()
                      )}
                    </Avatar>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                      onChange={handleFileInputChange}
                      style={{ display: 'none' }}
                      tabIndex={-1}
                      aria-hidden
                    />
                    <Tooltip title="Click or drag to upload profile picture" arrow>
                      <IconButton
                        component="span"
                        disabled={uploadingPicture}
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          bgcolor: 'white',
                          color: 'primary.main',
                          width: 40,
                          height: 40,
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            transform: 'scale(1.1)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {uploadingPicture ? (
                          <CircularProgress size={20} />
                        ) : (
                          <CameraAlt fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    {isDragging && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '3px dashed white',
                        }}
                      >
                        <CloudUpload sx={{ fontSize: 48, color: 'white' }} />
                      </Box>
                    )}
                  </Box>
                  
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 0.5,
                      color: getBackgroundImageUrl() ? 'white' : '#0f172a',
                      textAlign: 'center',
                      letterSpacing: '-0.01em',
                      textShadow: getBackgroundImageUrl() ? '0 1px 2px rgba(0,0,0,0.2)' : 'none',
                    }}
                  >
                    {getFullName()}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mb: 2,
                      color: getBackgroundImageUrl() ? 'rgba(255, 255, 255, 0.95)' : '#334155',
                      textAlign: 'center',
                      fontSize: '0.875rem',
                    }}
                  >
                    {user.email}
                  </Typography>
                  <Chip
                    label={planLabel}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      ...planChipSx,
                      '& .MuiChip-label': {
                        color: planChipSx.color,
                      },
                    }}
                  />
                </Box>

                {!isEditing && (
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Edit />}
                    onClick={() => setIsEditing(true)}
                    sx={{
                      mt: 'auto',
                      bgcolor: getBackgroundImageUrl() ? 'white' : '#0d9488',
                      color: getBackgroundImageUrl() ? 'primary.main' : 'white',
                      fontWeight: 600,
                      py: 1.5,
                      borderRadius: 2,
                      border: getBackgroundImageUrl() ? 'none' : `1px solid ${alpha('#0d9488', 0.35)}`,
                      '&:hover': {
                        bgcolor: getBackgroundImageUrl() ? 'rgba(255, 255, 255, 0.95)' : '#0f766e',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Edit Profile
                  </Button>
                )}
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        {/* Form Section */}
        <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
          <Fade in timeout={1000} style={{ width: '100%' }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                width: '100%',
                border: '1px solid',
                borderColor: alpha('#1d4ed8', 0.15),
                boxShadow: PROFILE_SURFACE_SHADOW,
              }}
            >
              <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Person sx={{ color: 'primary.main', fontSize: 28 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', letterSpacing: '-0.01em' }}>
                    Personal Information
                  </Typography>
                </Box>

                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                            <Person sx={{ color: 'action.active' }} />
                          </Box>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: isEditing ? 'translateY(-2px)' : 'none',
                            boxShadow: isEditing ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
                          },
                          '&.Mui-focused': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                            <Person sx={{ color: 'action.active' }} />
                          </Box>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: isEditing ? 'translateY(-2px)' : 'none',
                            boxShadow: isEditing ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
                          },
                          '&.Mui-focused': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      disabled
                      InputProps={{
                        readOnly: true,
                        startAdornment: (
                          <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                            <Email sx={{ color: 'action.active' }} />
                          </Box>
                        ),
                        endAdornment: (
                          <Tooltip title="Email cannot be changed" arrow>
                            <Lock sx={{ color: 'action.disabled', fontSize: 20, ml: 1 }} />
                          </Tooltip>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: isEditing ? 'translateY(-2px)' : 'none',
                            boxShadow: isEditing ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
                          },
                          '&.Mui-focused': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Read-only Information */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1.5 }} />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        p: 2,
                        height: '100%',
                        minHeight: 88,
                        borderRadius: 2,
                        bgcolor: alpha('#1d4ed8', 0.04),
                        borderColor: alpha('#1d4ed8', 0.15),
                        transition: 'all 0.25s ease',
                        '&:hover': {
                          bgcolor: alpha('#1d4ed8', 0.08),
                          borderColor: alpha('#1d4ed8', 0.25),
                          boxShadow: '0 2px 8px rgba(29, 78, 216, 0.15)',
                        },
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                        <Box sx={{ 
                          p: 0.5, 
                          borderRadius: 1, 
                          bgcolor: alpha('#1d4ed8', 0.1),
                          display: 'flex',
                          alignItems: 'center',
                        }}>
                          <Business sx={{ color: 'primary.main', fontSize: 20 }} />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Organization
                        </Typography>
                      </Stack>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary', letterSpacing: '0.05em' }}>
                        {(organizationName || user.organizationSlug || 'N/A').toUpperCase()}
                      </Typography>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        p: 2,
                        height: '100%',
                        minHeight: 88,
                        borderRadius: 2,
                        bgcolor: alpha('#0d9488', 0.04),
                        borderColor: alpha('#0d9488', 0.15),
                        transition: 'all 0.25s ease',
                        '&:hover': {
                          bgcolor: alpha('#0d9488', 0.08),
                          borderColor: alpha('#0d9488', 0.25),
                          boxShadow: '0 2px 8px rgba(13, 148, 136, 0.15)',
                        },
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                        <Box sx={{ 
                          p: 0.5, 
                          borderRadius: 1, 
                          bgcolor: alpha('#0d9488', 0.1),
                          display: 'flex',
                          alignItems: 'center',
                        }}>
                          <CalendarToday sx={{ color: 'secondary.main', fontSize: 20 }} />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Member Since
                        </Typography>
                      </Stack>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {formatDate(user.createdAt)}
                      </Typography>
                    </Card>
                  </Grid>

                  {/* Action Buttons */}
                  {isEditing && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1.5 }} />
                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap', pt: 0.5 }}>
                        <Button
                          variant="outlined"
                          onClick={handleCancel}
                          disabled={loading}
                          startIcon={<Cancel />}
                          sx={{
                            minWidth: 120,
                            py: 1.2,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: 2,
                            },
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                          onClick={handleSave}
                          disabled={loading}
                          sx={{
                            minWidth: 150,
                            py: 1.2,
                            background: PROFILE_BRAND_GRADIENT,
                            '&:hover': {
                              background: PROFILE_BRAND_GRADIENT_HOVER,
                              transform: 'translateY(-2px)',
                              boxShadow: '0 6px 20px rgba(29, 78, 216, 0.28)',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          {loading ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        {/* Password Change Section */}
        <Grid item xs={12}>
          <Fade in timeout={1200}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: alpha('#1d4ed8', 0.15),
                boxShadow: PROFILE_SURFACE_SHADOW,
              }}
            >
              <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' }, 
                  justifyContent: 'space-between', 
                  gap: 2,
                  mb: 3,
                  pb: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      p: 1, 
                      borderRadius: 2, 
                      bgcolor: alpha('#1d4ed8', 0.08),
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      <Lock sx={{ fontSize: 24, color: 'primary.main' }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', letterSpacing: '-0.01em' }}>
                        Security Settings
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        Manage your password and account security
                      </Typography>
                    </Box>
                  </Box>
                  {!isChangingPassword && (
                    <Button
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={() => setIsChangingPassword(true)}
                      sx={{
                        alignSelf: { xs: 'stretch', sm: 'center' },
                        minWidth: { sm: 160 },
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: 2,
                        },
                      }}
                    >
                      Change Password
                    </Button>
                  )}
                </Box>

                {isChangingPassword ? (
                  <Grid container spacing={2.5} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Current Password"
                        name="currentPassword"
                        inputRef={currentPasswordInputRef}
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={passwordData.currentPassword}
                        onChange={handlePasswordInputChange}
                        error={!!passwordErrors.currentPassword}
                        helperText={passwordErrors.currentPassword}
                        InputProps={{
                          startAdornment: (
                            <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                              <Lock sx={{ color: 'action.active' }} />
                            </Box>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                                onClick={() =>
                                  togglePasswordFieldVisibility(currentPasswordInputRef, setShowCurrentPassword)
                                }
                                onMouseDown={(e) => e.preventDefault()}
                                edge="end"
                              >
                                {showCurrentPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            },
                            '&.Mui-focused': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="New Password"
                        name="newPassword"
                        inputRef={newPasswordInputRef}
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordData.newPassword}
                        onChange={handlePasswordInputChange}
                        error={!!passwordErrors.newPassword}
                        helperText={passwordErrors.newPassword || '8-16 chars, uppercase, lowercase, number, special character'}
                        inputProps={{ maxLength: 16 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                                onClick={() =>
                                  togglePasswordFieldVisibility(newPasswordInputRef, setShowNewPassword)
                                }
                                onMouseDown={(e) => e.preventDefault()}
                                edge="end"
                              >
                                {showNewPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            },
                            '&.Mui-focused': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Confirm New Password"
                        name="confirmPassword"
                        inputRef={confirmPasswordInputRef}
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordInputChange}
                        error={!!passwordErrors.confirmPassword}
                        helperText={passwordErrors.confirmPassword}
                        inputProps={{ maxLength: 16 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                onClick={() =>
                                  togglePasswordFieldVisibility(confirmPasswordInputRef, setShowConfirmPassword)
                                }
                                onMouseDown={(e) => e.preventDefault()}
                                edge="end"
                              >
                                {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            },
                            '&.Mui-focused': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1.5 }} />
                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap', pt: 0.5 }}>
                        <Button
                          variant="outlined"
                          onClick={handleCancelPasswordChange}
                          disabled={changingPassword}
                          startIcon={<Cancel />}
                          sx={{
                            minWidth: 120,
                            py: 1.2,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: 2,
                            },
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={changingPassword ? <CircularProgress size={20} color="inherit" /> : <Save />}
                          onClick={handleChangePassword}
                          disabled={changingPassword}
                          sx={{
                            minWidth: 150,
                            py: 1.2,
                            background: PROFILE_BRAND_GRADIENT,
                            '&:hover': {
                              background: PROFILE_BRAND_GRADIENT_HOVER,
                              transform: 'translateY(-2px)',
                              boxShadow: '0 6px 20px rgba(29, 78, 216, 0.28)',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          {changingPassword ? 'Changing...' : 'Change Password'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                ) : (
                  <Alert 
                    severity="info" 
                    sx={{ 
                      borderRadius: 2,
                      '& .MuiAlert-message': { width: '100%' },
                    }}
                  >
                    <Typography variant="body2">
                      Your password was last updated on <strong>{formatDate(lastPasswordChangeLabel(user))}</strong>. Keep your password secure and change it regularly.
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Fade>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Profile;
